import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, ArrowRight, CreditCard, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";
import { getIcon } from "@/lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const BENEFITS = [
  { iconKey: "botb1_icon", titleKey: "botb1_title", descKey: "botb1_desc" },
  { iconKey: "botb2_icon", titleKey: "botb2_title", descKey: "botb2_desc" },
  { iconKey: "botb3_icon", titleKey: "botb3_title", descKey: "botb3_desc" },
  { iconKey: "botb4_icon", titleKey: "botb4_title", descKey: "botb4_desc" },
];

const STATS = [
  { numKey: "bots_stat1_num", labelKey: "bots_stat1_label" },
  { numKey: "bots_stat2_num", labelKey: "bots_stat2_label" },
  { numKey: "bots_stat3_num", labelKey: "bots_stat3_label" },
];

const PLAN_FEATURES = ["bots_plan_f1", "bots_plan_f2", "bots_plan_f3"];

export const BotsService = () => {
  const { t } = useSiteContent();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        plan_id: "bots",
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (err) {
      toast.error("No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  const rawPrice = (t('bots_price') || "").trim();
  const isNum = /^\d+([.,]\d+)?$/.test(rawPrice);

  return (
    <section id="bots" className="relative py-28 px-6" data-testid="bots-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-indigo-400 font-medium">
            <MessageSquare className="h-4 w-4" /> Nuevo servicio
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('bots_title')} <span className="text-gradient">{t('bots_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('bots_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = getIcon(t(b.iconKey));
            return (
              <motion.div
                key={b.titleKey}
                data-testid={`bot-benefit-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-7 hover:border-white/20 transition-colors duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 mb-5">
                  <Icon className="h-6 w-6 text-indigo-300" />
                </span>
                <h3 className="text-lg font-semibold mb-2">{t(b.titleKey)}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{t(b.descKey)}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 grid grid-cols-3 gap-4 max-w-3xl mx-auto">
          {STATS.map((s) => (
            <div key={s.numKey} data-testid={`bot-stat-${s.numKey}`} className="glass rounded-2xl py-6 px-4 text-center">
              <div className="text-3xl sm:text-4xl font-bold text-gradient">{t(s.numKey)}</div>
              <div className="mt-1 text-white/55 text-xs sm:text-sm">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>

        {/* Dedicated bot plan card */}
        <motion.div
          id="plan-bots"
          data-testid="bots-plan-card"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="mt-14 max-w-xl mx-auto rounded-3xl p-8 border border-indigo-400/40 bg-gradient-to-b from-indigo-500/[0.1] to-violet-500/[0.04] shadow-[0_8px_40px_rgba(99,102,241,0.18)] scroll-mt-28"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold">{t('bots_plan_name')}</h3>
              <p className="text-white/55 text-sm mt-1">{t('bots_subtitle')}</p>
            </div>
            <div className="text-right shrink-0">
              <span className="text-3xl font-bold text-gradient">{isNum ? `${rawPrice}€` : rawPrice}</span>
              {isNum && <span className="text-white/50 text-sm">/mes</span>}
            </div>
          </div>
          <ul className="mt-6 flex flex-col gap-2.5">
            {PLAN_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-white/75">
                <Check className="h-4 w-4 text-emerald-400 shrink-0" /> {t(f)}
              </li>
            ))}
          </ul>
          <Button
            data-testid="bots-subscribe-button"
            onClick={handleCheckout}
            disabled={loading}
            className="mt-7 w-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white h-12 gap-2 text-base border-0"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
            Suscribirme al bot
          </Button>
          <button
            data-testid="bots-contact-link"
            onClick={() => scrollTo("contact")}
            className="mt-4 w-full text-center text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
          >
            {t('bots_cta')} <ArrowRight className="inline h-4 w-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

